import { PocTable, PocTableRow } from 'commons/components/components-wrapper';
import { owcTheme } from 'core/theme/theme.config';
import styled, { css } from 'styled-components';
import { TooltipedCustomFieldCell } from './components/TooltipedCustomFieldCell.component';

const rowOptionsWidth = '120px';

export const setCSSVariable = (element: HTMLElement | null, variable: string, value: string) => {
    if (element) {
        element.style.setProperty(variable, value);
    }
};
export const OwcTableStyled = styled(PocTable)`
    --custom-table-sticky-top: var(--table-sticky-top, 220px);

    /* 60px is the TableHeaderLayout title height; the column header sticks right below it. */
    --custom-table-sticky-top-no-filters: var(
        --table-sticky-top-no-filters,
        calc(var(--custom-table-header-title-top, 25px) + 60px)
    );

    ${({ showSelectionColumn }) =>
        showSelectionColumn &&
        css`
            owc-table-row > owc-table-simple-cell {
                position: sticky;
            }
        `}
    ${({ stickyColumnBeginning = 0 }) =>
        stickyColumnBeginning > 0 &&
        Array.from(
            { length: stickyColumnBeginning },
            (_, index) => css`
                owc-table-row > owc-table-cell:nth-of-type(${index + 1}) {
                    position: sticky;
                }
            `
        )}

    /* OWC-OVERRIDE: freeze the grouped-table leading header cell so it stays aligned with the
       body's frozen leading cells during horizontal scroll.
       owc-table has no per-column sticky prop in v7.6.0. Revisit on the next @one upgrade. */
    ${({ showGroupExpanderColumn }) =>
        showGroupExpanderColumn &&
        css`
            owc-table-header > owc-table-header-cell.owc-table-header__leading-cell {
                position: sticky;
                left: 0;
                z-index: calc(var(--table-zindex-header-cell-sticky-base, 1000) + 1);
                background-color: ${owcTheme['one-color-interaction-default-intensity-low']};
            }
        `}


    owc-backdrop {
        background-color: #fff;
    }

    > owc-card {
        display: unset;
        overflow: unset;
        border-bottom: none;

        /* OWC-OVERRIDE: flatten the wrapping div[slot='header'] so the sticky title bar isn't
           clamped to the short header box.
           owc-card gives the header slot its own grid area in v7.6.0 with no opt-out prop.
           Revisit on the next @one upgrade. */
        > [slot='header'] {
            display: contents;
        }

        /* OWC-OVERRIDE: owc-table sets an inline overflow on the merged content/table wrapper;
           force it visible so the sticky header sticks to the outer scroll container, not
           mid-table. !important is required because the value is set inline by owc-table in
           v7.6.0, and neither owc-table nor owc-card exposes an overflow prop. Revisit on the next @one upgrade. */
        > .owc-card__content-wrapper.owc-table__table-wrapper {
            position: relative;
            overflow: visible !important;

            .owc-table-body {
                owc-table-cell {
                    box-shadow: inset 0 -1px 0 0 var(--table-horizontal-border-color);
                }
            }
        }

        div[slot='footer'] {
            padding: 0 !important;

            owc-component-footer {
                padding: 0.5rem 1rem !important;
            }
        }
    }

    /* OWC-OVERRIDE: owc-table's header and body default to min-width: fit-content and spill
       out of the card; constrain them so content scrolls inside the container instead, and
       drop the sticky-edge shadow owc paints on the last frozen header cell.
       owc-table exposes no horizontal-scroll or max-width prop in v7.6.0. Revisit on the next @one upgrade. */
    &.horizontal-scroll owc-card {
        > .owc-card__content-wrapper {
            owc-table-header {
                min-width: 0;
                max-width: 100%;
            }

            .owc-table-body {
                min-width: 0;
                max-width: 100%;
                overflow: auto;
                scrollbar-width: none;

                &::-webkit-scrollbar {
                    display: none;
                }
            }

            .owc-table-header-cell.owc-table-header-cell--sticky-last-of-start::after {
                background-color: unset;
            }
        }
    }

    /* OWC-OVERRIDE: flatten owc-component-header and its nested wrappers so they don't clamp
       the sticky title bar (its sticky parent must be the tall owc-card).
       owc-component-header always renders these wrapper divs in v7.6.0 and offers no
       unstyled/bare variant, so display:contents is the only way to remove them from the
       layout tree. !important beats owc's scoped display rules. Revisit on the next @one upgrade. */
    owc-component-header {
        display: contents !important;

        .owc-component-header__prefix-container {
            display: contents !important;

            .owc-component-header__prefix-container__title {
                display: contents !important;
            }
        }

        > div {
            width: 100%;
        }
    }

    /* The .horizontal-scroll block above targets owc-table-header at a higher specificity.
       Reordering the two would change the cascade, so the rule is suppressed here instead. */
    /* stylelint-disable-next-line no-descending-specificity */
    owc-table-header {
        /* OWC-OVERRIDE: pin the sticky column header below the sticky title bar. owc-table
           sets its own top on the header in v7.6.0 and exposes no sticky-offset prop, so
           !important is required. */
        top: calc(var(--custom-table-sticky-top) + var(--custom-table-sticky-top-no-filters)) !important;
        overflow: hidden;
        border-top: 1px solid rgb(194 186 181);

        &.no-filters {
            top: var(--custom-table-sticky-top-no-filters) !important;
        }

        /* OWC-OVERRIDE: the header cell is a grid whose fixed 48px row top-aligns single-line
           labels; size it to content and centre the grid items so single- and multi-line
           headers are both vertically centred (as in v6).
           owc-table-header-cell exposes no alignment or auto-height prop in v7.6.0. */
        owc-table-header-cell {
            align-items: center;
            height: auto;
        }

        /* OWC-OVERRIDE: owc forces the slot-wrapper to height:100% (filling the 48px row),
           which blocks the vertical centring above; size it to its content instead.
           !important is required to beat owc's own height:100% rule in v7.6.0. */
        .owc-table-header-cell__slot-wrapper {
            height: auto !important;
        }

        /* OWC-OVERRIDE: grey caption text in header cells. owc-typography has no colour prop
           and owc-table-header-cell no header-text-colour hook in v7.6.0. */
        .owc-table-header-cell__slot-wrapper > div {
            owc-typography[variant='caption'] {
                color: var(--one-color-gray-600);
            }
        }
    }

    /* OWC-OVERRIDE: restore v6's grey striping on group header rows. owc's own rule paints
       them white at a higher specificity than this selector can reach without !important,
       and owc-table-group exposes no header-background prop in v7.6.0. Revisit on the next @one upgrade. */
    .owc-table-group__header-wrapper {
        background-color: ${owcTheme['one-color-gray-50']} !important;
    }

    /* OWC-OVERRIDE: drop the trailing gap owc reserves in the group header slot so the
       group cells line up with the body columns. No spacing prop in v7.6.0. */
    .owc-table-group__header-wrapper__sticky-wrapper__header-slot-wrapper {
        padding-right: unset !important;
    }

    /* OWC-OVERRIDE: keep the group header's icon and tooltip text at regular weight; owc
       bolds all group-header content in v7.6.0 with no weight prop to opt out. */
    .owc-table-group > .owc-table-group__header-wrapper > [slot='header-grid'] > div :first-child {
        owc-icon,
        owc-tooltip .owc-tooltip__body {
            font-weight: normal !important;
        }
    }

    /* OWC-OVERRIDE: grey caption text in group headers, matching the column headers above.
       owc-typography has no colour prop in v7.6.0. */
    .owc-table-group > .owc-table-group__header-wrapper > [slot='header-grid'] > div > div {
        owc-typography[variant='caption'] {
            color: var(--one-color-gray-600);
        }
    }

    /* OWC-OVERRIDE: let opted-in cells fill the column. owc-table-cell sizes its content
       container to content in v7.6.0 and exposes no full-width prop. */
    .full_width {
        .owc-table-cell__content-container {
            width: 100%;
        }
    }

    /* OWC-OVERRIDE: give the empty-state slot a minimum height so the "no results" message
       is not cramped. owc-table has no empty-state sizing prop in v7.6.0. */
    .owc-table-body [slot='empty'] {
        min-height: 200px;
    }
`;

export const TableHeaderLayout = styled.div`
    position: sticky;
    top: var(--custom-table-header-title-top, 25px);
    z-index: 110;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 60px;
    padding: 10px 16px;
    outline: solid 1px var(--one-color-background-contrast);

    /* When the title sticks at the very top of the scroll container (top: 0), its top outline is
       clipped by the container's overflow; this optional border keeps a visible top line. */
    border-top: var(--custom-table-header-title-top-border, none);
    background-color: #fff;
    border-radius: 0.25rem 0.25rem 0 0;
`;

export const TableHeaderTitleLayout = styled.div`
    owc-typography {
        padding-right: 4px;
    }

    .header-extra-info::before {
        display: inline-block;
        height: 100%;
        padding-right: 3px;
        font-size: 2rem;
        vertical-align: middle;
        content: '·';
    }
`;

export const TableHeaderOptionsLayout = styled.div``;

export const CustomTableHeaderRows = styled.div`
    position: sticky;
    top: 0;
    z-index: 100;
    display: flex;
    flex-direction: column;
    background-color: ${owcTheme['one-color-background-base']};
`;

export const CustomTableLayout = styled.div`
    display: flex;
    flex-direction: column;
`;

export const CustomTableGroupHeaderViewport = styled.div`
    width: 100%;
    overflow: hidden;
`;

export const CustomTableGroupHeaderGrid = styled.div<{ $gridTemplateColumns: string }>`
    display: grid;
    grid-template-columns: ${({ $gridTemplateColumns }) => $gridTemplateColumns};
    width: var(--table-width, 100%);
    min-width: var(--table-width, 100%);
`;

export const CustomTableGroupHeaderCell = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    min-width: 0;
    padding: ${owcTheme['one-spacer-px-8']} ${owcTheme['one-spacer-px-16']};
    color: ${owcTheme['one-color-gray-600']};
    background-color: ${owcTheme['one-color-gray-50']};

    /* Vertical separators between groups (design shows a divider at each group boundary). */
    &:not(:first-child) {
        border-left: 1px solid ${owcTheme['one-color-gray-200']};
    }
`;

export const OwcTableRowStyled = styled(PocTableRow)<{ $hovered?: boolean; $interactive?: boolean }>`
    position: relative;
    cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};

    /* OWC-OVERRIDE: collapse the row grid to one full-height track so cells fill the row
       (don't use grid-row: 1/-1 — it reorders the selection checkbox). owc-table-row builds
       its own grid in v7.6.0 with no row-template prop; !important beats owc's scoped rule. */
    grid-template-rows: 100% !important;

    /* Fill the virtualized wrapper height so short rows reach the next row; no !important so
       stage rows keep their inline height. */
    height: 100%;

    /* Keep the implicit row (the row-options overlay) at 0 height so the overlay doesn't
       grow the row on hover. */
    grid-auto-rows: 0;

    /* NOTE: .owc-table-row-options / .owc-table-row-option are OUR OWN class names, applied
       by custom-table-row.component.tsx and training-programs-list-group.component.tsx.
       Despite the prefix they are not owc internals — @one/web-components 7.6.0 defines
       neither — so the rules below are ordinary app styling, not OWC overrides.

       Overlay the hover actions on the row's right edge without displacing cells: place them
       in the implicit grid row 2 and pull up with translateY(-100%); sticky right: 0 keeps
       them in view during horizontal scroll. */
    > .owc-table-row-options {
        position: sticky;
        top: 0;
        right: 0;
        grid-row: 2;
        grid-column: 1 / -1;
        place-self: start end;
        height: var(--custom-table-row-height, 100%);
        transform: translateY(-100%);
    }

    @media (pointer: coarse) {
        .owc-table-row-options {
            opacity: 1;

            > div {
                background-color: #fff;
            }
        }

        owc-table-cell:last-of-type > div {
            width: calc(100% - ${rowOptionsWidth});

            > span {
                display: unset;
            }
        }
    }

    ${({ $hovered }) =>
        $hovered &&
        css`
            &.with-row-options {
                .owc-table-row-options {
                    opacity: 1;
                    transition: opacity 0.5s;
                }

                owc-table-cell:last-of-type > div {
                    width: calc(100% - ${rowOptionsWidth});
                }
            }
        `}

    &.drawer-opened {
        background-color: var(--one-color-interaction-active-brand-lower);
    }

    /* OWC-OVERRIDE: tint not-yet-started rows, but leave owc's own selected and hover
       backgrounds alone. owc-table-row exposes no per-row background prop in v7.6.0, so the
       selected state has to be matched by its internal modifier class. */
    &.not-started:not(.owc-table-row--selected, :hover) {
        background-color: var(--one-color-gray-50);
    }

    owc-table-cell {
        cursor: unset;

        & > div {
            display: block;
            padding-bottom: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    &.hidden-selector {
        owc-checkbox {
            display: none;
        }
    }
`;

export const CellListElement = styled.span`
    display: flex;
`;

export const TooltipedMoreIndicatorStyled = styled(TooltipedCustomFieldCell)`
    padding-left: 4px;
    color: #00f;
`;

export const OwcTableRowOptionsStyled = styled.div`
    /* Shared base for row hover actions; positioning is set by each consumer (grid row vs
       group row). .owc-table-row-option is our own class name, not an owc internal — see the
       note on OwcTableRowStyled. */
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 0;
    height: 100%;
    background: var(--one-color-interaction-hover-brand-3);
    background-color: inherit;
    opacity: 0;

    owc-button {
        max-width: 45px;
        background-color: inherit;
    }

    > .owc-table-row-option {
        background-color: inherit;
    }

    button {
        color: ${owcTheme['one-color-interaction-default-intensity-medium']};
        background-color: transparent;
    }

    owc-icon-button {
        cursor: pointer;

        :hover {
            background-color: var(--one-color-interaction-hover-brand-low) !important;
        }
    }

    &.hide {
        display: none;
    }
`;

export const OwcTableGroupRowStyled = styled.div`
    overflow: hidden;
    cursor: pointer;

    /* .owc-table-row-options is our own class name, not an owc internal — see the note on
       OwcTableRowStyled. */
    .owc-table-row-options {
        position: absolute;
        right: 0;
    }

    @media (pointer: coarse) {
        .owc-table-row-options {
            opacity: 1;

            > div {
                background-color: #fff;
            }
        }

        .table-group-cell:last-of-type > div {
            width: calc(100% - ${rowOptionsWidth});

            > span {
                display: unset;
            }
        }
    }

    &.with-row-options:hover {
        .owc-table-row-options {
            width: ${rowOptionsWidth};
            opacity: 1;
            transition: opacity 0.5s;
        }

        .table-group-cell:last-of-type > div {
            width: calc(100% - ${rowOptionsWidth});
        }
    }
`;

export const OwcTableGroupRowCellStyled = styled.div``;

export const CustomHorizontalTableScrollContainer = styled.div`
    position: sticky;
    bottom: 0;
    z-index: 100;
    width: 100%;
    overflow-x: auto;
`;

export const CustomHorizontalTableScroll = styled.div`
    width: var(--table-width, 100%);
    height: 1px;
    clip-path: inset(100%);
`;

export const FooterContainer = styled.div`
    border: 0.0625rem solid var(--one-color-background-contrast);
    border-top: none;
`;
